import { useState } from 'react'
import { useNotifications } from '@/contexts/NotificationContext'

const TIME_OF_DAY_OPTIONS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
]

// Formats an "HH:MM" 24-hour string as a 12-hour display time (e.g. "2:30 PM").
function formatReminderTime(str) {
  const match = /^(\d{1,2}):(\d{2})$/.exec((str || '').trim())
  if (!match) return ''
  const hour = Number(match[1])
  const minute = match[2]
  const h12 = hour % 12 || 12
  const ampm = hour >= 12 ? 'PM' : 'AM'
  return `${h12}:${minute} ${ampm}`
}

function MedicationTracker({ medications = [], onAddMedication, onUpdateMedication, onDeleteMedication }) {
  const { addToast } = useNotifications()
  const [formOpen, setFormOpen] = useState(medications.length === 0)
  const [medName, setMedName] = useState('')
  const [timeOfDay, setTimeOfDay] = useState('morning')
  const [reminderTime, setReminderTime] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editTimeOfDay, setEditTimeOfDay] = useState('morning')
  const [editReminderTime, setEditReminderTime] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    if (!medName.trim()) return

    const result = await onAddMedication({
      id: crypto.randomUUID(),
      name: medName.trim(),
      frequency: 'daily',
      timeOfDay,
      reminderTime,
    })

    if (result?.error) {
      addToast({
        title: "Couldn't save medication",
        body: 'Please check your connection and try again.',
        overdueItems: [],
      })
      return
    }

    setMedName('')
    setTimeOfDay('morning')
    setReminderTime('')
  }

  function handleDelete(medId) {
    if (confirmDeleteId !== medId) {
      setConfirmDeleteId(medId)
      return
    }
    onDeleteMedication(medId)
    setConfirmDeleteId(null)
  }

  function startEdit(med) {
    setEditingId(med.id)
    setEditName(med.name)
    setEditTimeOfDay(med.timeOfDay)
    setEditReminderTime(med.reminderTime || '')
    setConfirmDeleteId(null)
  }

  async function handleSaveEdit() {
    if (!editName.trim()) return
    const result = await onUpdateMedication(editingId, {
      name: editName.trim(),
      timeOfDay: editTimeOfDay,
      reminderTime: editReminderTime,
    })

    if (result?.error) {
      addToast({
        title: "Couldn't save changes",
        body: 'Please check your connection and try again.',
        overdueItems: [],
      })
      return
    }

    setEditingId(null)
  }

  function handleCancelEdit() {
    setEditingId(null)
  }

  const grouped = {
    morning: medications.filter((m) => m.timeOfDay === 'morning'),
    afternoon: medications.filter((m) => m.timeOfDay === 'afternoon'),
    evening: medications.filter((m) => m.timeOfDay === 'evening'),
  }

  return (
    <div className="page medication-tracker">
      <div className="collapsible-section">
        <button
          type="button"
          className="collapsible-header"
          onClick={() => setFormOpen((prev) => !prev)}
        >
          <h2 className="page-title">Add Medication</h2>
          <span className={`collapsible-chevron${formOpen ? ' collapsible-chevron--open' : ''}`}>
            &#9662;
          </span>
        </button>

        {formOpen && (
          <div className="collapsible-body">
            <p className="page-description">
              Add your daily medications to track adherence throughout the week.
            </p>

            <form className="task-form" onSubmit={handleSubmit}>
              <input
                type="text"
                className="task-input"
                placeholder="Medication name (e.g. Adderall XR 20mg)"
                value={medName}
                onChange={(e) => setMedName(e.target.value)}
              />
              <select
                className="task-input task-priority-input"
                value={timeOfDay}
                onChange={(e) => setTimeOfDay(e.target.value)}
              >
                {TIME_OF_DAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <label className="med-time-input-label">
                Exact reminder time <span className="med-time-input-hint">(optional)</span>
                <input
                  type="time"
                  className="task-input"
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                  aria-label="Exact reminder time (optional)"
                />
              </label>
              <button type="submit" className="btn btn-primary">
                Add Medication
              </button>
            </form>
          </div>
        )}
      </div>

      {medications.length === 0 ? (
        <div className="task-list-empty">
          <p className="placeholder-text">
            No medications added yet. Add your daily medications above to start tracking.
          </p>
        </div>
      ) : (
        <div className="med-timetable">
          {TIME_OF_DAY_OPTIONS.map(({ value, label }) => {
            const meds = grouped[value]
            if (meds.length === 0) return null
            return (
              <section key={value} className="med-time-section">
                <h3 className="med-time-header">{label}</h3>
                <ul className="med-list">
                  {meds.map((med, i) => (
                    <li key={med.id} className="med-item stagger-item" style={{ animationDelay: `${i * 60}ms` }}>
                      {editingId === med.id ? (
                        <>
                          <input
                            type="text"
                            className="task-input med-edit-input"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            autoFocus
                          />
                          <select
                            className="task-input task-priority-input med-edit-select"
                            value={editTimeOfDay}
                            onChange={(e) => setEditTimeOfDay(e.target.value)}
                          >
                            {TIME_OF_DAY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <input
                            type="time"
                            className="task-input med-edit-time"
                            value={editReminderTime}
                            onChange={(e) => setEditReminderTime(e.target.value)}
                            aria-label="Exact reminder time (optional)"
                          />
                          <button className="btn-confirm-yes" onClick={handleSaveEdit}>Save</button>
                          <button className="btn-confirm-no" onClick={handleCancelEdit}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <span className="med-item-name">{med.name}</span>
                          <span className="med-item-freq">
                            {med.reminderTime ? formatReminderTime(med.reminderTime) : 'Daily'}
                          </span>
                          <button
                            className="btn-icon btn-edit-subtask"
                            onClick={() => startEdit(med)}
                            title="Edit medication"
                          >
                            &#9998;
                          </button>
                          {confirmDeleteId === med.id ? (
                            <span className="delete-confirm">
                              <span className="delete-confirm-text">Remove?</span>
                              <button className="btn-confirm-yes" onClick={() => handleDelete(med.id)}>Yes</button>
                              <button className="btn-confirm-no" onClick={() => setConfirmDeleteId(null)}>No</button>
                            </span>
                          ) : (
                            <button
                              className="btn-icon btn-delete-subtask"
                              onClick={() => handleDelete(med.id)}
                              title="Remove medication"
                            >
                              &#10005;
                            </button>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default MedicationTracker
